import Backbone from 'backbone'
import renderChartDesigner from 'scoreboard/charts/render-chart-designer'
import initiativeChart from 'initiative-chart'
import formatValue from 'scoreboard/components/format-value'
import moment from 'moment'
import momentHelper from 'scoreboard/components/moment-helper'
import template from './historical-graph.hbs'
import _ from 'lodash'
import ColorLegend from 'scoreboard/charts/color-legend'
import i18n from 'scoreboard/components/i18n'
import DataType from 'scoreboard/datasets/model/data-type'
const { CURRENCY } = DataType

var debug = false

var HistoricalGraph = Backbone.View.extend({

	className: 'historical-graph-container',

	/**
	 *
	 * @param opts.width
	 * @param opts.height
	 * @param {Boolean} [opts.legend=true] Render the legend or not
	 */
	initialize: function (opts={}) {
		this.options = opts
		this.width = opts.width
		this.height = opts.height
	},

	render: function() {
		if (this.$el.children().length === 0) {
			this.$el.empty().append(template())
		}

		var plots = this._plots()
		this._renderLegend(plots)
		this._renderChart(plots)

		return this
	},

	_plots: function () {
		var currencyCode = this.model.get('currencyCode')

		var currencyFormatter = function (shorten, val) {
			return formatValue({
				value: val,
				dataType: CURRENCY,
				currencyCode: currencyCode,
				shorten
			})
		}

		var series = []

		let expendedPoints = this.model.expendedSeriesPoints()
		expendedPoints.forEach(p => p.formatted = currencyFormatter(false, p.raw))

		series.push({
			id: 'cost',
			label: i18n.get('initiatives.chart.actualCost'),
			type: 'line',
			color: colors.cost,
			axis: 'left',
			formatter: currencyFormatter.bind(null, true),
			points: expendedPoints
		})

		series.push({
			id: 'performed',
			label: i18n.get('initiatives.chart.earnedValue'),
			type: 'line',
			color: colors.performed,
			axis: 'right',
			formatter: percentFormatter,
			points: this.model.percentCompleteSeriesPoints()
		})

		// Turn bcws (the goal) into a % so that it goes 0-100%, then stick it
		// on the right axis with the % complete.
		let bcwsPoints = this.model.get('budgetedCostForWorkScheduled')
		let maxBcws = _(bcwsPoints).map('value').max()

		var goalSeries = {
			id: 'goal',
			label: i18n.get('initiatives.chart.goal'),
			type: 'area',
			color: colors.goal,
			axis: 'right',
			formatter: percentFormatter,
			points: _.map(bcwsPoints, function (bcws) {
				let rawVal = bcws.value / maxBcws
				return {
					title: bcws.date,
					// #43680: rawVal can be NaN, and that will cause trouble with the
					// chart lib because NaN is not equal to undefined - even with
					// coercion. This fakes the lib into thinking there is a raw value
					// when there is not.
					raw: _.isFinite(rawVal) ? rawVal : undefined
				}
			})
		}

		series.push(goalSeries)

		// #52298 since we're "faking" a chart designer, we need to set the
		// underlyingSerieId to the same fake id we created. This particularly
		// helps with emphasizing a serie on hover
		series.forEach(s => s.underlyingSerieId = s.id)

		return [ { series } ]
	},

	/**
	 * Resizes the chart SVG, using the given dimensions
	 */
	resize: function (dim) {
		this.width = dim.width
		this.height = dim.height

		this._renderChart(this._plots())
	},

	_renderChart: function (plots, opt) {
		let series = plots[0].series

		if (opt && opt.emphasize) {
			_.forEach(series, s => {
				if (s.id !== opt.emphasize.id) {
					s.deemphasize = true
				}
			})
		}
		var budget = this.model.get('aggregatedBudget')
		var goalSeries = series[2]
		var workPerformedPoints = this.model.get('budgetedCostForWorkPerformed')
		var goalPoints = this.model.get('budgetedCostForWorkScheduled')
		var costPoints = this.model.get('actualCostOfWorkPerformed')

		// All the points should have been ordered by date, so this should be safe
		var latestGoalDate = moment(goalPoints[goalPoints.length - 1].date)

		let latestNonGoalPoint = _.last(workPerformedPoints)
		let latestNonGoalDate = latestNonGoalPoint ? moment(latestNonGoalPoint.date) : null

		if (latestNonGoalDate && latestNonGoalDate.isAfter(latestGoalDate)) {
			// Fill in weekly goal points so that it continues to the last non-goal point.
			// The Goal stays flat after the initiative end date.
			var lastGoalValue = goalSeries.points[goalSeries.points.length - 1].raw

			var date = latestGoalDate
			while (date.isBefore(latestNonGoalDate)) {
				date.add(7, 'days')

				if (date.isAfter(latestNonGoalDate)) {
					// Incrementing a week skipped us past the last non-goal point,
					// so just use the last non-goal point instead
					date = latestNonGoalDate
				}

				goalSeries.points.push({
					title: momentHelper.isoDate(date),
					raw: lastGoalValue
				})
			}
		}

		var maxSpent = _(costPoints).map('value').max() || 0

		var data = {
			plots,
			tooltip: {
				header: {
					formatter: momentHelper.formatDate
				}
			},
			axes: {
				bottom: {
					formatter: function (date) {
						var _date = moment(date)
						return {
							title: _date.format('MMM D'),
							subtitle: _date.format('YYYY')
						}
					}
				},
				right: {
					// Make sure right axis goes to highest point, either 100%
					// or % over budget
					yMaxForce: Math.max(1, maxSpent / budget)
				},
				left: {
					// Make sure left axis goes to highest point, either the total budget
					// or the amount spent (if over budget)
					yMaxForce: Math.max(budget, maxSpent)
				}
			}
		}

		if (debug) {
			console.log(JSON.stringify(data, null, '    '))
		}

		let chartContainer = this.$('.chart-container')

		// Note: using jQuery's outerHeight() instead of something like getBoundingClientRect(), because
		// we specifically **don't** want to take dashboard scaling into account
		let legendHeight = this.$('.legend').outerHeight() || 0
		data.height = this.height  - legendHeight
		data.width = this.width

		if (this.options.legend) {
			// This will overwrite the height set for exports, so only apply
			// this if a legend is actually being rendered
			chartContainer.height('calc(100% - ' + legendHeight + 'px)')
		}

		var renderOptions = _.extend({
			data,
			container: chartContainer,
			chartViewObject: this.chartViewObject,
			doRender: ({ data, container }) => {
				return initiativeChart(container[0], data)
			}
		}, opt)

		this.chartViewObject = renderChartDesigner.renderOnly(renderOptions)
	},

	_renderLegend: function (plots) {
		if (!this.options.legend) {
			return
		}

		this._colorLegend && this._colorLegend.remove()

		this._colorLegend = new ColorLegend({
			labels: true,
			series: plots[0].series,
			legendType: 'chart-title'
		})

		this.$('.legend').append(this._colorLegend.el)
		this._colorLegend.render()

		this._colorLegend.on('emphasize deemphasize', this.emphasize.bind(this))
	},

	emphasize: function (seriesId) {
		// #37574 re-enable this once serie emphasis is re-implemented
		this._renderChart(this._plots(), {
			emphasize: seriesId
		})
	},

	remove: function () {
		Backbone.View.prototype.remove.apply(this, arguments)
		this.chartViewObject && this.chartViewObject.destroy()
		this._colorLegend && this._colorLegend.remove()
	}

})

function percentFormatter (value) {
	return formatValue({
		value: value,
		dataType: 'percent',
		precision: 0
	})
}

var colors = {
	goal: 'lighter-gray',
	cost: 'initiative-budget',
	performed: 'initiative-schedule'
}

function canDisplay (initiative) {
	// #23123 need start/end dates, a budget, and goal points (budgetedCostForWorkScheduled)
	// to render a chart. Note that budgetedCostForWorkPerformed and actualCostOfWorkPerformed
	// will be null if no statuses have been entered yet
	return !!(initiative.get('aggregatedStartDate') && initiative.get('aggregatedEndDate')
		&& initiative.get('aggregatedBudget') && initiative.get('budgetedCostForWorkScheduled'))
}

export default HistoricalGraph
export { canDisplay }

HistoricalGraph.__filename__ = 'initiatives/overview/historical-graph.js'
